import React, { FC, useContext, useEffect, useState, ChangeEvent, useCallback } from 'react';

import {
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';

import { GlobalContext } from '../../../../../../../../store';
import { IRoundGame } from '../../../../../../../../store/admin/eventRounds/store';
import { HANDICAP_METRIC } from '../../../../../../../../store/admin/events/store';

import { PlayerMetaDataSectionStyled } from '../PlayerMetaDataSection.Styles';
import { toHandicap } from '../../../../../../../../contrib/Utils/toHandicap';
import { useRouteInfoState } from '../../../../../../../../components/RouteInfo/Context/RouteInfo.context';
import { useGetAdminEvent } from '../../../../../../../../contrib/Hooks/useGetAdminEvent';
import { mapSelectIndexStateIndicators } from './Utils/mapSelectIndexStateIndicators';
import { useBoolean } from '../../../../../../../common/Hooks/useBoolean.hook';
import { IndexInputStyled } from './Components/IndexInput.Styles';

interface IProps {
  game: IRoundGame | undefined;
}

export const SelectIndex: FC<IProps> = props => {
  const { game } = props;

  const {
    actions: {
      adminEventRounds: { updateGames },
      adminEventEnrollments: { edit: editAdminEventEnrollment }
    },
    playerEditorModal: { enrollment }
  } = useContext(GlobalContext);

  const { clubId, eventId, roundId } = useRouteInfoState();

  const event = useGetAdminEvent(eventId);

  const [selectedHandicapMetric, setSelectedHandicapMetric] = useState<HANDICAP_METRIC>(
    HANDICAP_METRIC.CURRENT_GHIN
  );
  const [manualIndexInputValue, setManualIndexInputValue] = useState<string>('0');

  const [lowHandicap, setLowHandicap] = useState<string>();
  const [currentHandicap, setCurrentHandicap] = useState<string>();
  const [sectionState, setSectionState] = useState<'idle' | 'loading' | 'saved'>('idle');

  const [firstHandicapMetricSetWasDone, setFirstHandicapMetricSetWasDone] = useBoolean(false);

  useEffect(() => {
    if (enrollment) {
      const { handicapLow, handicapCurrent } = enrollment.user.profile;

      setLowHandicap(toHandicap(handicapLow));
      setCurrentHandicap(toHandicap(handicapCurrent));
    }
  }, [enrollment]);

  useEffect(() => {
    if (event && enrollment && game && lowHandicap && !firstHandicapMetricSetWasDone) {
      const newHandicapMetric = enrollment.manualHandicap
        ? HANDICAP_METRIC.MANUAL
        : Number(game.index) === Number(lowHandicap)
        ? HANDICAP_METRIC.LOWEST_GHIN
        : HANDICAP_METRIC.CURRENT_GHIN;

      setFirstHandicapMetricSetWasDone(true);
      setSelectedHandicapMetric(newHandicapMetric);
    }
  }, [event, enrollment, game, lowHandicap]);

  useEffect(() => {
    if (
      game &&
      enrollment &&
      enrollment.manualHandicap &&
      selectedHandicapMetric === HANDICAP_METRIC.MANUAL
    ) {
      setManualIndexInputValue(String(game.index).replace('-', '+'));
    }
  }, [enrollment, game, selectedHandicapMetric]);

  const saveGame = useCallback(
    async (handicapMetric: HANDICAP_METRIC) => {
      if (game && clubId && eventId && roundId && enrollment) {
        const { handicapLow, handicapCurrent } = enrollment.user.profile;

        const mapIndex: Record<number, string> = {
          [HANDICAP_METRIC.MANUAL]: manualIndexInputValue,
          [HANDICAP_METRIC.CURRENT_GHIN]: handicapCurrent || '',
          [HANDICAP_METRIC.LOWEST_GHIN]: handicapLow || ''
        };

        const index = mapIndex[handicapMetric];

        return updateGames(clubId, eventId, roundId, [{ ...game, index: index.replace('+', '-') }]);
      }
    },
    [game, clubId, eventId, roundId, manualIndexInputValue, enrollment]
  );

  const saveManualGame = () => {
    setSectionState('loading');

    saveGame(HANDICAP_METRIC.MANUAL)
      .then(() =>
        editAdminEventEnrollment(clubId, eventId, {
          ...enrollment,
          manualHandicap: true
        })
      )
      .then(() => {
        setSectionState('saved');

        setTimeout(() => setSectionState('idle'), 3 * 1000);
      })
      .catch(() => {
        // TODO: manage errors somehow
        setSectionState('idle');
      });
  };

  const onManualIndexInputChange = (evnt: ChangeEvent<HTMLInputElement>) => {
    const index = evnt.target.value;

    const handicapRegex = /^\+?\d?(\d+(.?\d?\d?))?$/;
    const handicapMatch = index.match(handicapRegex);

    if ((!handicapMatch || index.includes('-')) && index !== '') {
      return manualIndexInputValue;
    }

    setManualIndexInputValue(index);
  };

  const onSelectHandicapMetric = (_: ChangeEvent<HTMLInputElement>, value: string) => {
    setSelectedHandicapMetric(Number(value));

    if (Number(value) !== HANDICAP_METRIC.MANUAL) {
      setSectionState('loading');
      setManualIndexInputValue('0');

      saveGame(Number(value))
        .then(() =>
          editAdminEventEnrollment(clubId, eventId, {
            ...enrollment,
            manualHandicap: false
          })
        )
        .then(() => {
          setSectionState('saved');

          setTimeout(() => setSectionState('idle'), 3 * 1000);
        })
        .catch(() => {
          // TODO: manage errors somehow
          setSectionState('idle');
        });
    }
  };

  return (
    <PlayerMetaDataSectionStyled>
      <FormControl classes={{ root: 'formcontrol-container' }} component="fieldset">
        <FormLabel component="legend">
          <p>SELECT INDEX:</p>

          {mapSelectIndexStateIndicators[sectionState]}
        </FormLabel>

        <RadioGroup
          classes={{ root: 'radiogroup-container' }}
          value={selectedHandicapMetric}
          onChange={onSelectHandicapMetric}
        >
          <div className="index-type-container">
            <FormControlLabel
              disabled={sectionState === 'loading'}
              value={HANDICAP_METRIC.MANUAL}
              control={<Radio color="primary" disabled={!game} />}
              label="Manual"
            />

            <div className="spacer" />

            <IndexInputStyled
              disabled={!game || Number(selectedHandicapMetric) !== HANDICAP_METRIC.MANUAL}
              onChange={onManualIndexInputChange}
              type="text"
              value={manualIndexInputValue}
            />

            <IconButton
              aria-label="delete"
              color="primary"
              disabled={
                !game ||
                !manualIndexInputValue ||
                Number(selectedHandicapMetric) !== HANDICAP_METRIC.MANUAL ||
                manualIndexInputValue === '+'
              }
              onClick={saveManualGame}
            >
              <SaveIcon />
            </IconButton>
          </div>

          <div className="index-type-container">
            <FormControlLabel
              disabled={sectionState === 'loading'}
              value={HANDICAP_METRIC.CURRENT_GHIN}
              control={<Radio color="primary" disabled={!game} />}
              label="Current"
            />

            <div className="spacer" />

            {game && enrollment && <span className="disabled-index">{currentHandicap}</span>}

            <div className="spacer2" />
          </div>

          <div className="index-type-container">
            <FormControlLabel
              disabled={sectionState === 'loading'}
              value={HANDICAP_METRIC.LOWEST_GHIN}
              control={<Radio color="primary" disabled={!game} />}
              label="Low"
            />

            <div className="spacer" />

            {game && enrollment && <span className="disabled-index">{lowHandicap}</span>}

            <div className="spacer2" />
          </div>
        </RadioGroup>
      </FormControl>
    </PlayerMetaDataSectionStyled>
  );
};
